import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { TodayProposalView } from '@heraion/shared';

import { Button } from '../primitives/Button';
import type { UiTheme } from '../theme';
import { restingGroups } from './todayRestingLine';
import { useDateLocale } from '../primitives/DateFormatContext';

// The «today» card with nothing in it: the fact (who rests and until when, one
// line per return day, or that no muscle is under its weekly minimum), never an
// instruction, and the two exits. A button without its callback is not drawn.

type Props = {
  proposal: TodayProposalView;
  weeklyMinimumMet: boolean;
  building: boolean;
  onBuildAnyway?: () => void;
  onOpenCreateHub?: () => void;
  theme: UiTheme;
};

export function TodayDraftEmpty({
  proposal,
  weeklyMinimumMet,
  building,
  onBuildAnyway,
  onOpenCreateHub,
  theme,
}: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const styles = makeStyles(theme);
  const groups = restingGroups(proposal.restingMuscles, t, dateLocale);
  const showBox = groups.length > 0 || weeklyMinimumMet;
  return (
    <>
      {showBox ? (
        // The box appears after the coverage loads: said politely, not on focus.
        <View style={styles.box} accessibilityLiveRegion="polite">
          {groups.map((g) => (
            <Text key={g.text} style={styles.line} accessibilityLabel={g.label}>
              {g.text}
            </Text>
          ))}
          {weeklyMinimumMet ? (
            <Text style={styles.line}>{t('mobile.logger.draftToday.atMinimum')}</Text>
          ) : null}
        </View>
      ) : null}
      {onBuildAnyway ? (
        <Button
          theme={theme}
          label={t('mobile.logger.draftToday.buildAnyway')}
          onPress={onBuildAnyway}
          loading={building}
          full
        />
      ) : null}
      {onOpenCreateHub ? (
        <Button
          theme={theme}
          variant="secondary"
          label={t('mobile.logger.draftToday.newWorkout')}
          onPress={onOpenCreateHub}
          full
        />
      ) : null}
    </>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Same box as the draft's rows card.
    box: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s5,
      gap: s.s3,
    },
    // The fact the card exists to say: body size and ink, not the footnote's.
    line: { color: p.ink, fontSize: ty.sizes.base, lineHeight: ty.sizes.base * 1.4 },
  });
}
